import { useId, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";
import { useStore } from "../../lib/store";
import { salvaClassificacoes } from "../classifications/save";
import { showFeedback } from "../../lib/feedback";
import { useModalOverlay } from "../../components/overlay";
import { DiscardConfirm, useDiscardPolicy } from "../../ui/Dialog";
import { Field } from "../../ui/Field";
import { Select } from "../../ui/Select";
import { TextInput } from "../../ui/Field";
import {
  emptyLeadForm,
  formFieldsOf,
  leadFormToDraft,
  stepOfError,
  validateLeadForm,
  type LeadFormValues,
} from "./form";
import { LeadFormStep } from "./LeadFormFields";
import { LeadPhotoField } from "./LeadPhotoField";

/**
 * Gaveta "Novo lead" do protótipo Zênite, aberta pelo botão do topo em qualquer
 * página.
 *
 * Os campos vêm da MESMA definição usada pela edição (`LeadFormStep`), que é o
 * que garante a equivalência exigida pela matriz do lead (AGENTS §19.1). A
 * composição é a do protótipo: identificação, valor e prioridade à vista;
 * contato, marcadores e o formulário do funil atrás de "Informações
 * adicionais". A foto é enviada depois que o ID existe, no mesmo fluxo
 * perceptível e com falha tratada (§7.1).
 */
export function NewLeadDrawer({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (leadId: string) => void;
}) {
  const store = useStore();
  const containerRef = useRef<HTMLElement>(null);
  const titleId = useId();
  const funnelLabelId = useId();

  const funnels = useMemo(() => store.funnels.filter((funnel) => !funnel.archived), [store.funnels]);
  const [funnelId, setFunnelId] = useState(funnels[0]?.id ?? "");
  const [values, setValues] = useState<LeadFormValues>(() => emptyLeadForm());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [focusKey, setFocusKey] = useState<string | undefined>(undefined);
  const [more, setMore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");
  const [photo, setPhoto] = useState<File | null>(null);

  const fields = formFieldsOf(store.leadFields, funnelId);
  const stageId = store.stages
    .filter((stage) => stage.funnelId === funnelId && !stage.archived)
    .sort((a, b) => a.order - b.order)[0]?.id;

  const dirty =
    values.name.trim().length > 0 ||
    values.company.trim().length > 0 ||
    values.role.trim().length > 0 ||
    values.potentialValue.trim().length > 0 ||
    Object.keys(values.fieldValues).length > 0 ||
    photo !== null;

  const { asking, setAsking, onBackdrop } = useDiscardPolicy(dirty, onClose);
  useModalOverlay({ containerRef, onClose, active: !asking });

  const identities = useMemo(() => {
    if (!store.identityFoundationAvailable) return [];
    const companies = new Map(store.companies.map((company) => [company.id, company]));
    return store.contacts
      .filter((contact) => !contact.mergedIntoId && companies.has(contact.companyId))
      .slice(0, 8)
      .map((contact) => {
        const company = companies.get(contact.companyId)!;
        return {
          id: contact.id,
          label: `${company.tradeName || company.legalName} · ${contact.name}`,
          company: company.tradeName || company.legalName,
          name: contact.name,
          role: contact.role ?? "",
          segment: company.segment ?? "",
        };
      });
  }, [store.companies, store.contacts, store.identityFoundationAvailable]);

  const submit = async () => {
    const found = validateLeadForm(values, fields);
    setErrors(found);
    const firstKey = Object.keys(found)[0];
    if (firstKey) {
      if (stepOfError(firstKey, fields) !== "identificacao") setMore(true);
      setFocusKey(firstKey);
      return;
    }
    if (!funnelId || !stageId) return;
    setSaving(true);
    try {
      const draft = leadFormToDraft(values);
      const leadId = await store.addLead(
        { ...draft.lead, fieldValues: draft.fieldValues },
        funnelId,
        stageId,
        note.trim() || undefined,
      );
      // Classificação escolhida no cadastro: o comando é por eixo e roda depois
      // da criação, que é quem devolve o ID do lead (§19.4).
      await salvaClassificacoes(store, leadId, {}, values.classifications);
      if (photo) {
        const ok = await store.setLeadPhoto(leadId, photo);
        if (!ok) {
          showFeedback("O lead foi criado, mas a foto não pôde ser enviada. Tente de novo pelo perfil.", "error");
        }
      }
      onCreated(leadId);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="zn-backdrop" role="presentation" onMouseDown={onBackdrop}>
        <aside
          ref={containerRef}
          className="zn-drawer"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
        >
          <div className="drawer-head">
            <div>
              <h2 id={titleId}>Novo lead</h2>
              <div className="dh-sub">
                Campos com <span className="req">✦</span> são obrigatórios
              </div>
            </div>
            <button type="button" className="zn-icon-btn" aria-label="Fechar" title="Fechar" onClick={onClose}>
              <X size={16} aria-hidden="true" />
            </button>
          </div>

          <div className="drawer-body">
            <LeadPhotoField file={photo} onChange={setPhoto} />

            <LeadFormStep
              step="identificacao"
              funnelId={funnelId}
              values={values}
              errors={errors}
              focusKey={focusKey}
              onChange={(patch) => setValues((current) => ({ ...current, ...patch }))}
              afterRole={
                identities.length > 0 ? (
                  <details className="z-identity-picker">
                    <summary className="z-help">Usar empresa e contato já cadastrados</summary>
                    <div className="z-identity-options">
                      {identities.map((identity) => (
                        <button
                          key={identity.id}
                          type="button"
                          className="btn-ghost"
                          onClick={() =>
                            setValues((current) => ({
                              ...current,
                              name: identity.name,
                              company: identity.company,
                              role: current.role || identity.role,
                              segment: current.segment || identity.segment,
                            }))
                          }
                        >
                          {identity.label}
                        </button>
                      ))}
                    </div>
                  </details>
                ) : null
              }
            />

            <div className="cx-form-grid" style={{ marginTop: 12 }}>
              <div className="cx-field">
                <span id={funnelLabelId} className="cx-field-label">
                  Funil inicial
                </span>
                <Select
                  size="field"
                  labelledBy={funnelLabelId}
                  options={funnels.map((funnel) => ({ value: funnel.id, label: funnel.name }))}
                  value={funnelId || null}
                  onChange={(value) => setFunnelId(value)}
                />
              </div>
            </div>

            <button
              type="button"
              className="more-toggle"
              aria-expanded={more}
              onClick={() => setMore((value) => !value)}
            >
              <span className="mt-left">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M8 12h8" />
                </svg>
                Informações adicionais
              </span>
              <span className="chev">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </span>
            </button>

            {more && (
              <div className="z-form-extra">
                <p className="sec-label">Contato</p>
                <LeadFormStep
                  step="contato"
                  funnelId={funnelId}
                  values={values}
                  errors={errors}
                  focusKey={focusKey}
                  onChange={(patch) => setValues((current) => ({ ...current, ...patch }))}
                />
                <p className="sec-label" style={{ marginTop: 18 }}>
                  Negócio
                </p>
                <LeadFormStep
                  step="negocio"
                  funnelId={funnelId}
                  values={values}
                  errors={errors}
                  focusKey={focusKey}
                  onChange={(patch) => setValues((current) => ({ ...current, ...patch }))}
                />
                <div className="cx-form-grid" style={{ marginTop: 12 }}>
                  <Field
                    label="Comentário inicial"
                    className="cx-span-2"
                  >
                    {({ id }) => (
                      <TextInput id={id} value={note} onChange={(event) => setNote(event.target.value)} />
                    )}
                  </Field>
                </div>
              </div>
            )}

            {Object.keys(errors).length > 0 && (
              <p className="cx-form-alert" role="alert">
                Revise os campos destacados antes de criar o lead.
              </p>
            )}
          </div>

          <div className="drawer-foot">
            <button type="button" className="btn-ghost" onClick={onClose}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={saving}
              aria-busy={saving || undefined}
              onClick={() => void submit()}
            >
              {saving ? "Criando…" : "Criar lead"}
            </button>
          </div>
        </aside>
      </div>
      <DiscardConfirm
        open={asking}
        onKeep={() => setAsking(false)}
        onDiscard={() => {
          setAsking(false);
          onClose();
        }}
      />
    </>
  );
}
